import type { FormVersion, Rule } from "../state/formVersion";
import { RuleActionKey, useFormVersionDispatch, useFormVersionState } from "../state/formVersion";
import { initialized } from "../utils/initializedUtil";
import type { FieldConfig, VariableConfig } from "../fields/types";
import { VariableState } from "../fields/types";
import VariableId from "../variables/VariableId";
import type { VariableType } from "../variables/types";
import type { SetStoreFunction } from "solid-js/store";
import { useTranslation } from "../state/i18n";
import type { TFunction } from "i18next";
import DataNameService from "./DataNameService";
import { type Accessor, batch, createMemo, on } from "solid-js";
import toVariableDefinition from "../variables/toVariableDefinition";
import { isEmpty, isNil, lowerFirst } from "lodash-es";
import { trackStore } from "@solid-primitives/deep";
import VariableValueReference from "../variables/VariableValueReference";
import type { Integration } from "../integrations/types";
import FeatureService from "./FeatureService";

type VariableUsedByItem =
  | { kind: "field"; field: FieldConfig }
  | { kind: "integration"; integration: Integration }
  | { kind: "rule"; rule: Rule };

let setFormVersionStore: SetStoreFunction<FormVersion> | null = null;
let formVersionStore: FormVersion | null = null;
let tInstance: TFunction | null = null;
let usageMap: Accessor<Map<string, VariableUsedByItem[]>> | null = null;

const getSetFormVersionStore = () => initialized(setFormVersionStore, "setFormVersionStore");
const getFormVersionStore = () => initialized(formVersionStore, "formVersionStore");

const getT = () => initialized(tInstance, "t");

const init = () => {
  setFormVersionStore = useFormVersionDispatch();
  formVersionStore = useFormVersionState();
  tInstance = useTranslation().t;

  const containsVariableReference = (value: unknown): boolean => {
    if (isNil(value) || typeof value !== "object") {
      return false;
    }
    if (value instanceof VariableValueReference) {
      return true;
    }
    return Object.values(value).some(containsVariableReference);
  };

  const ruleContainsVariableReference = (rule: Rule): boolean =>
    rule.conditions.some((condition) => condition.type === "VARIABLE") ||
    rule.actions.some((action) => action.key === RuleActionKey.REFERENCE && action.value instanceof VariableId);

  // Pre-calculate this expensive operation instead of doing it per-variable
  // oxlint-disable-next-line solid/reactivity
  usageMap = createMemo(
    on(
      [
        () => trackStore(getFormVersionStore().fields),
        () => trackStore(getFormVersionStore().integrations),
        () => trackStore(getFormVersionStore().rules),
      ],
      ([fields, integrations, rules]) =>
        getUsedBy({
          fields: fields.filter((field) => containsVariableReference(field.properties)) as FieldConfig[],
          integrations: integrations.filter((integration) => containsVariableReference(integration.configuration)),
          rules: rules.filter(ruleContainsVariableReference),
        }),
    ),
  );
};

const getUsedBy = (input: { fields: FieldConfig[]; integrations: Integration[]; rules: Rule[] }) => {
  const usageMap = new Map<string, VariableUsedByItem[]>();

  const addToUsageMap = (variableId: string, usage: VariableUsedByItem) => {
    if (!usageMap.has(variableId)) {
      usageMap.set(variableId, []);
    }
    usageMap.get(variableId)!.push(usage);
  };
  const extractVariableReferences = (value: unknown): VariableId[] => {
    const references: VariableId[] = [];
    const search = (value: unknown) => {
      if (isNil(value) || typeof value !== "object") {
        return;
      }
      const objectValue = value as Record<string, unknown>;
      if (objectValue instanceof VariableValueReference) {
        references.push(objectValue.id);
      }
      for (const key in objectValue) {
        search(objectValue[key]);
      }
    };
    search(value);
    return references;
  };
  const extractVariableReferencesFromRule = (rule: Rule): VariableId[] => [
    ...rule.conditions.flatMap((condition) => (condition.type === "VARIABLE" ? [condition.variableId] : [])),
    ...rule.actions.flatMap((action) =>
      action.key === RuleActionKey.REFERENCE && action.value instanceof VariableId ? [action.value] : [],
    ),
  ];

  input.fields.forEach((field) => {
    const variableReferences = extractVariableReferences(field.properties);
    variableReferences.forEach((variableId) => {
      addToUsageMap(variableId.toString(), { kind: "field", field });
    });
  });

  input.integrations.forEach((integration) => {
    const variableReferences = extractVariableReferences(integration.configuration);
    variableReferences.forEach((variableId) => {
      addToUsageMap(variableId.toString(), { kind: "integration", integration });
    });
  });

  input.rules.forEach((rule) => {
    const variableReferences = extractVariableReferencesFromRule(rule);
    variableReferences.forEach((variableId) => {
      addToUsageMap(variableId.toString(), { kind: "rule", rule });
    });
  });

  return usageMap;
};

const usedBy = (id: VariableId): VariableUsedByItem[] => {
  if (isNil(usageMap?.())) {
    return [];
  }
  return usageMap()?.get(id.toString()) ?? [];
};

export type VariableServiceType = {
  createVariable: (type: VariableType) => VariableConfig;
  addVariable: (variable: VariableConfig) => void;
  deleteVariable: (id: VariableId) => void;

  getVariable: (id: VariableId) => VariableConfig | undefined;
  getVariables: () => VariableConfig[];
  getVariablesByTypes: (types: VariableType[]) => VariableConfig[];

  usedBy: (variableId: VariableId) => VariableUsedByItem[];

  setName: (id: VariableId, name: string) => void;
  setDataName: (
    id: VariableId,
    dataName: string,
    skipWhenPublished?: boolean,
    dataNameManuallyEdited?: boolean,
  ) => void;
  setDefaultValue: <T extends string | number | boolean | undefined>(id: VariableId, defaultValue: T) => void;
  showVariables: () => boolean;
};

const getVariables = () => getFormVersionStore().variables;

const showVariables = () => FeatureService.hasFeature("FORM_VARIABLES") || !isEmpty(getFormVersionStore().variables);

const setName = (id: VariableId, value: string) => {
  const currentVariable = getVariables().find((variable) => variable.uid.equals(id));

  batch(() => {
    setProperty(id, "name", value);
    if (currentVariable?.dataNameManuallyEdited !== true) {
      setDataName(id, lowerFirst(value), true);
    }
  });
};

const setDataName = (id: VariableId, value: string, skipWhenPublished = false, dataNameManuallyEdited?: boolean) => {
  const predicate = (variable: VariableConfig) =>
    variable.uid.equals(id) && !(skipWhenPublished && variable.state === VariableState.PUBLISHED);

  batch(() => {
    getSetFormVersionStore()("variables", predicate, "dataName", () =>
      DataNameService.getValidDataNameForVariable(value, id),
    );

    if (dataNameManuallyEdited !== undefined) {
      getSetFormVersionStore()("variables", predicate, "dataNameManuallyEdited", dataNameManuallyEdited);
    }
  });
};

const setDefaultValue = <T extends string | number | boolean | undefined>(id: VariableId, value: T) =>
  setProperty(id, "defaultValue", value);

const setProperty = (id: VariableId, key: keyof VariableConfig, value: unknown) =>
  getSetFormVersionStore()("variables", (variable) => variable.uid.equals(id), key, value);

const create = (): VariableServiceType => ({
  createVariable: (type: VariableType) => {
    const definition = toVariableDefinition(type);
    return {
      uid: VariableId.random(),
      name: getT()(definition.titleKey),
      type: definition.type,
      dataName: DataNameService.getValidDataNameForVariable(lowerFirst(getT()(definition.titleKey))),
      dataNameManuallyEdited: false,
      defaultValue: undefined,
      state: VariableState.UNPUBLISHED,
    };
  },
  addVariable: (variable: VariableConfig) => getSetFormVersionStore()("variables", (prev) => [...prev, variable]),
  deleteVariable: (id: VariableId) =>
    getSetFormVersionStore()("variables", (prev) => prev.filter((variable) => !id.equals(variable.uid))),

  getVariable: (id: VariableId) => getVariables().find((variable) => id.equals(variable.uid)),
  getVariables,
  getVariablesByTypes: (types: VariableType[]) => getVariables().filter((variable) => types.includes(variable.type)),
  setName,
  usedBy,
  setDataName,
  setDefaultValue,
  showVariables,
});

const unscopedVariableService = create();

export default {
  init,
  create,
  ...unscopedVariableService,
};
